/** Intercom article that explains Foodback Intelligence, incl. its AI Act position. */
export const AI_DISCLOSURE_ARTICLE_ID = 12320249;

/**
 * `generative` covers anything an LLM wrote (summaries, sentiment prose, chat answers).
 * `statistical` covers Foodback Intelligence features that are regression, not generative AI —
 * they are branded the same but must not claim to be AI-generated.
 */
export type AiDisclosureKind = 'generative' | 'statistical' | 'chat' | 'translation';

/**
 * `chip` sits beside a title, `note` under the generated content it describes, and `icon` is the
 * mark alone — for cards too narrow to hold the name without wrapping onto its own line. The `icon`
 * variant leans on its tooltip to supply the name, so it is only valid where that tooltip renders.
 */
export type AiDisclosureVariant = 'chip' | 'icon' | 'note';

/** Long enough that dragging the cursor across a card does not flash the panel. */
export const AI_DISCLOSURE_TOOLTIP_DELAY_MS = 400;

const ROOT = 'SHARED.AI_DISCLOSURE';

export const AI_DISCLOSURE_KEYS = {
  learnMore: `${ROOT}.LEARN_MORE`,
  // What Foodback Intelligence is. Deliberately generic: the chip always explains the feature, never
  // the surface it sits on — a card's own content is documented by its info icon.
  aboutIntro: `${ROOT}.ABOUT_INTRO`,
  aboutBody: `${ROOT}.ABOUT_BODY`,
  // The compact marker leads with the brand, which is what Foodback's published AI Act position
  // promises for dashboard elements ("carries the Foodback Intelligence marker"). The `(AI)` suffix
  // is what makes it a disclosure rather than a product name; the statistical variant omits it
  // because a regression is not AI-generated.
  label: {
    generative: 'SHARED.SYSTEM.FOODBACK_INTELLIGENCE_AI',
    statistical: 'SHARED.SYSTEM.FOODBACK_INTELLIGENCE',
    chat: 'SHARED.SYSTEM.FOODBACK_INTELLIGENCE_AI',
    // Repeated once per language in a translation list, so it stays short and drops the brand.
    translation: `${ROOT}.TRANSLATION_LABEL`,
  },
  body: {
    generative: `${ROOT}.BODY`,
    statistical: `${ROOT}.STATISTICAL_BODY`,
    chat: `${ROOT}.CHAT`,
    translation: `${ROOT}.TRANSLATION_BODY`,
  },
} as const satisfies {
  learnMore: string;
  aboutIntro: string;
  aboutBody: string;
  label: Record<AiDisclosureKind, string>;
  body: Record<AiDisclosureKind, string>;
};
